import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendUp, FileText, Printer, PencilSimpleLine } from "@phosphor-icons/react";
import { printPdfBlob } from "@/lib/printPdf";
import RequestsList from "./RequestsList";
import { Button } from "@/components/ui/button";
import { formatEUR, formatDate } from "@/lib/constants";
import ComparativeRentEditor from "./ComparativeRentEditor";
import { StatusPill, BASIS_LABEL } from "./riMeta";

function Row({ k, v, testId }) {
  return <><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 tabular-nums" data-testid={testId}>{v}</dd></>;
}

function Result({ c }) {
  return (
    <dl className="grid grid-cols-[220px_1fr] gap-x-3 gap-y-1 text-[13px]" data-testid="ri-result">
      <Row k="Aktuelle Nettokaltmiete" v={`${formatEUR(c.current_rent)}${c.current_per_m2 ? ` (${c.current_per_m2.toFixed(2)} €/m²)` : ""}`} testId="ri-current" />
      <Row k="Wohnfläche" v={c.living_area ? `${c.living_area} m²` : "—"} />
      <Row k={`Sperrfrist ab ${c.sperrfrist_anchor_label || ""}`} v={c.sperrfrist_anchor ? formatDate(c.sperrfrist_anchor) : "—"} />
      <Row k="Frühester Versand" v={c.earliest_send_date ? formatDate(c.earliest_send_date) : "—"} testId="ri-send-date" />
      <Row k="Frühestens wirksam ab" v={c.earliest_effective_date ? formatDate(c.earliest_effective_date) : "—"} testId="ri-effective-date" />
      <Row k={`Kappungsgrenze ${c.cap_pct ?? ""} %`} v={c.cap_limit != null ? `${formatEUR(c.cap_limit)} (Basis ${formatEUR(c.cap_base_rent)} am ${formatDate(c.cap_base_date)}${c.cap_modernisation_eur ? ` + Modernisierung ${formatEUR(c.cap_modernisation_eur)}` : ""})` : "—"} testId="ri-cap-limit" />
      <Row k="Grenze Vergleichsmiete" v={c.ovm_limit != null ? `${formatEUR(c.ovm_limit)} (${c.ovm_per_m2_used?.toFixed(2)} €/m²)` : "—"} testId="ri-ovm-limit" />
      {c.increase_eur > 0 && <Row k="Neue Miete (max.)" v={<b className="text-emerald-700">{formatEUR(c.new_rent)} · +{formatEUR(c.increase_eur)} (+{c.increase_pct} %) — begrenzt durch {c.limited_by}</b>} testId="ri-new-rent" />}
    </dl>
  );
}

function IndexResult({ c }) {
  return (
    <div className="space-y-3" data-testid="rent-increase-panel">
      <div className="flex flex-wrap items-center gap-3">
        <TrendUp size={20} className="text-slate-700" />
        <h3 className="font-semibold text-slate-900">Indexmiete – Anpassung nach § 557b BGB</h3>
        <StatusPill status={c.status} label={c.status_label} />
      </div>
      <p className="text-sm text-slate-700" data-testid="ri-reason">{c.reason}</p>
      {c.warnings?.map((w) => <div key={w} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">{w}</div>)}
      <dl className="grid grid-cols-[220px_1fr] gap-x-3 gap-y-1 text-[13px]" data-testid="ri-index-result">
        <Row k="Aktuelle Nettokaltmiete" v={formatEUR(c.current_rent)} />
        <Row k={`Jahresfrist ab ${c.sperrfrist_anchor_label || ""}`} v={c.sperrfrist_anchor ? formatDate(c.sperrfrist_anchor) : "—"} />
        <Row k="VPI Basis" v={c.index_base_value ? `${c.index_base_value} (${c.index_base_period})` : "—"} />
        <Row k="VPI aktuell" v={c.index_current_value ? `${c.index_current_value} (${c.index_current_period})` : "—"} />
        <Row k="Indexänderung" v={c.index_change_pct != null ? `${c.index_change_pct} %` : "—"} testId="ri-index-pct" />
        <Row k="Frühester Versand" v={c.earliest_send_date ? formatDate(c.earliest_send_date) : "—"} />
        <Row k="Wirksam ab" v={c.earliest_effective_date ? formatDate(c.earliest_effective_date) : "—"} />
        {c.increase_eur > 0 && <Row k="Neue Miete" v={<b className="text-emerald-700">{formatEUR(c.new_rent)} · +{formatEUR(c.increase_eur)} (+{c.increase_pct} %)</b>} testId="ri-new-rent" />}
      </dl>
      <p className="text-xs text-slate-500">Keine Kappungsgrenze und keine Vergleichsmiete bei Indexmiete (§ 557b Abs. 2 BGB). Die Erklärung in Textform mit Angabe der Indexänderung erstellen Sie im Reiter „Vertrag“ unter „Indexmiete“.</p>
    </div>
  );
}

export default function RentIncreasePanel({ tenancyId, onCreateLetter, onOpenEditor, onPrinted }) {
  const [busy, setBusy] = useState("");
  const [reqKey, setReqKey] = useState(0);
  const [c, setC] = useState(null);
  const [basis, setBasis] = useState("high");
  const [amount, setAmount] = useState("");
  const load = useCallback(() => api.get(`/rent-increase/tenancy/${tenancyId}`, { params: { basis } }).then(({ data }) => setC(data)), [tenancyId, basis]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!c) return;
    const ov = c.running && ["draft", "pending_approval", "approved"].includes(c.running.status) ? c.running.override?.new_rent : null;
    const r = ov && ov <= c.new_rent ? ov : c.new_rent;
    setAmount(r ? Number(r).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
  }, [c]);
  const amountNum = () => { const t = String(amount).trim(); const n = Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t); return n > 0 ? n : null; };
  if (!c) return <div className="text-sm text-slate-500 p-4">Prüfe…</div>;
  if (c.mode === "index") return <IndexResult c={c} />;

  const cr = c.comparative_rent || {};
  const run = c.running;
  const runDraft = run && ["draft", "pending_approval", "approved"].includes(run.status);
  const runLocked = run && !runDraft;  // versendet/zugestimmt → keine neue Berechnung anzeigen
  const doPrint = async () => {
    setBusy("print");
    try {
      const { data } = await api.post(`/rent-increase/tenancy/${tenancyId}/print`, { new_rent: amountNum(), basis }, { responseType: "blob" });
      printPdfBlob(data); setReqKey((k) => k + 1); onPrinted?.();
      toast.success("Mieterhöhungsverlangen mit Zustimmungserklärung erzeugt und abgelegt");
    } catch (e) {
      let msg = "PDF konnte nicht erzeugt werden";
      try { msg = JSON.parse(await e?.response?.data?.text?.())?.detail || msg; } catch { /* Blob ohne JSON */ }
      toast.error(msg);
    } finally { setBusy(""); }
  };
  const doEditor = async () => {
    setBusy("editor");
    try {
      const { data } = await api.post(`/rent-increase/tenancy/${tenancyId}/editor`, { new_rent: amountNum(), basis });
      setReqKey((k) => k + 1); onOpenEditor?.(data);
      toast.success("Zustimmungserklärung als Anlage vorgemerkt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Editor konnte nicht geöffnet werden"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-4" data-testid="rent-increase-panel">
      <div className="flex flex-wrap items-center gap-3">
        <TrendUp size={20} className="text-slate-700" />
        <h3 className="font-semibold text-slate-900">Mieterhöhung zur ortsüblichen Vergleichsmiete (§ 558 BGB)</h3>
        {!runLocked && <StatusPill status={c.status} label={c.status_label} />}
        {!runLocked && (c.status === "possible_now" || c.status === "possible_from") ? (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" className="gap-1" onClick={doPrint} disabled={!!busy} data-testid="ri-print-btn"><Printer size={14} /> {busy === "print" ? "Erzeuge…" : runDraft ? "Entwurf drucken" : "Drucken"}</Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={doEditor} disabled={!!busy} data-testid="ri-editor-btn"><PencilSimpleLine size={14} /> {busy === "editor" ? "Bereite vor…" : runDraft ? "Entwurf im Editor öffnen" : "Im Editor öffnen"}</Button>
            <Button size="sm" variant="ghost" className="gap-1" onClick={onCreateLetter} data-testid="ri-create-letter"><FileText size={14} /> Amtliche Dokumente</Button>
          </div>
        ) : null}
      </div>
      {run && (
        <div className={`rounded-md border px-3 py-2 text-sm ${runLocked ? "border-indigo-200 bg-indigo-50 text-indigo-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="ri-running-banner">
          <b>Mieterhöhung läuft{run.case_number ? ` (Az. ${run.case_number})` : ""}.</b>{" "}
          {runLocked
            ? `Das Verlangen ist versendet – bis zum Abschluss wird keine weitere Mieterhöhung berechnet. Neue Miete ${formatEUR(run.new_rent)} ab ${formatDate(run.effective_date)}.`
            : "Die Berechnung unten ist die Grundlage des laufenden Entwurfs – Änderungen über „Entwurf öffnen“."}
        </div>
      )}
      {!runLocked && <p className="text-sm text-slate-700" data-testid="ri-reason">{c.reason}</p>}
      <RequestsList tenancyId={tenancyId} reloadKey={reqKey} onOpenDraft={onOpenEditor} onApplied={() => { load(); onPrinted?.(); }} />
      {c.warnings?.map((w) => <div key={w} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">{w}</div>)}
      {c.status !== "excluded" && !runLocked && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Result c={c} />
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">Ansatz:</span>
              {[["mid", "Mittelwert"], ["high", "Oberwert der Spanne"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setBasis(k)} data-testid={`ri-basis-${k}`}
                  className={`px-2 py-0.5 rounded border ${basis === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{l}</button>
              ))}
            </div>
            {(c.status === "possible_now" || c.status === "possible_from") && c.increase_eur > 0 && (
              <div className="rounded-md border border-teal-100 bg-teal-50/60 p-2 text-xs space-y-1" data-testid="ri-amount-box">
                <label className="flex flex-wrap items-center gap-2"><span className="font-medium text-slate-800">Verlangte Nettokaltmiete</span>
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} className="h-7 w-28 rounded border border-slate-300 px-2 text-right tabular-nums" data-testid="ri-amount-input" /> €
                  <button type="button" className="text-teal-700 hover:underline" onClick={() => setAmount(Number(c.new_rent).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))} data-testid="ri-amount-max">Höchstbetrag übernehmen</button></label>
                <div className="text-slate-600">Vorbelegt mit dem Höchstbetrag nach {basis === "high" ? "Oberwert" : "Mittelwert"} ({formatEUR(c.new_rent)}, {c.limited_by}) – diese Basis wird für Schreiben, Rückantwort und Vorgang übernommen. Ein niedrigerer fester Betrag ist jederzeit möglich – gilt für „Drucken“ und „Im Editor öffnen“.</div>
              </div>
            )}
            <div className="text-xs text-slate-700" data-testid="ri-tight-info">
              Kappungsgrenze {c.cap_pct} % — <span className="text-slate-500">{c.tight_market_reason}</span>{" "}
              <a href="/vergleichsmieten" className="text-teal-700 hover:underline" data-testid="ri-tight-link">in „Vergleichsmieten“ ändern</a>
            </div>
            {c.history?.length > 0 && (
              <div data-testid="ri-history">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Mietentwicklung</div>
                {c.history.map((h) => (
                  <div key={h.date} className="text-xs flex gap-2"><span className="w-20">{formatDate(h.date)}</span>
                    <span className="tabular-nums">{formatEUR(h.from)} → {formatEUR(h.to)}</span><span className="text-slate-500">{BASIS_LABEL[h.basis] || h.basis}</span></div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs font-medium text-slate-800 mb-2">Vergleichsmiete dieser Einheit{" "}
                {cr.level === "property" && <span className="text-slate-500 font-normal">(übernommen: Objektwert)</span>}
                {["district", "city"].includes(cr.level) && <span className="text-teal-700 font-normal" data-testid="ri-cr-inherited">(übernommen aus Vergleichsmieten-Liste: {cr.area_label}{cr.adjustment_eur ? `, Zu-/Abschlag ${cr.adjustment_eur} €` : ""})</span>}
              </div>
              <ComparativeRentEditor key={`u-${cr.level}-${cr.mid}`} level="unit" targetId={c.unit_id} unitId={c.unit_id} value={cr.mid ? cr : null} onSaved={load} />
              {cr.level && cr.level !== "unit" && <div className="text-[10px] text-slate-500 mt-1">Speichern legt einen eigenen Wert nur für diese Einheit fest (überschreibt den übernommenen Wert).</div>}
            </div>
            <details className="rounded-md border border-slate-200 p-3">
              <summary className="text-xs font-medium text-slate-800 cursor-pointer">Standardwert für das ganze Objekt {c.property_name}</summary>
              <div className="mt-2"><ComparativeRentEditor key={`p-${cr.mid}`} level="property" targetId={c.property_id} propertyId={c.property_id} value={cr.level === "property" ? cr : null} onSaved={load} /></div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}
